import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { AgeGroup } from '../../types/domain';
import {
  BodySide,
  InjectionSite,
  getSitesForAge,
  getSuggestedSiteCount,
  getInsulinTypeOptions,
  getSiteClass,
  getAgeGroupLabel
} from '../../lib/domain-data';

export default function InjectInsulin() {
  const { user, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve glucoseValue if passed from check-glucose page
  const passedGlucoseValue = location.state?.glucoseValue as number | undefined;

  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [bodySide, setBodySide] = useState<BodySide>('front');
  const [suggestedSiteId, setSuggestedSiteId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [insulinTypeId, setInsulinTypeId] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [injectionDate, setInjectionDate] = useState<string>('');
  const [injectionTime, setInjectionTime] = useState<string>('');
  const [insulinName, setInsulinName] = useState<string>('');
  const [insulinDose, setInsulinDose] = useState<number | ''>(1);

  const [isSavingInjection, setIsSavingInjection] = useState(false);
  const [saveInjectionError, setSaveInjectionError] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setInjectionDate(local.toISOString().slice(0, 10));
    setInjectionTime(local.toISOString().slice(11, 16));
  }, []);

  const loadInitialSuggestion = async (nextAgeGroup: AgeGroup | null, sites: InjectionSite[]) => {
    if (!nextAgeGroup || !user) {
      setSuggestedSiteId(null);
      return;
    }
    const nextId = await dataService.getLastSite(user.id, nextAgeGroup);

    let suggestion = 1;
    const count = sites.length;

    if (nextId && nextId >= 1 && nextId <= count) {
      suggestion = (nextId % count) + 1;
    }

    setSuggestedSiteId(suggestion);
    const site = sites.find(s => s.id === suggestion);
    if (site) {
      setBodySide(site.side);
    }
  };

  useEffect(() => {
    if (profile?.birth_date) {
      const birthDate = new Date(profile.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      const calculatedGroup: AgeGroup = age < 11 ? '2-10' : '11-plus';

      if (ageGroup !== calculatedGroup) {
        setAgeGroup(calculatedGroup);
        const sites = getSitesForAge(calculatedGroup);
        if (!suggestedSiteId) {
          loadInitialSuggestion(calculatedGroup, sites);
        }
      }
    }
  }, [profile, ageGroup, suggestedSiteId]);

  const currentSites = useMemo(() => getSitesForAge(ageGroup), [ageGroup]);

  const handleSelectInsulinType = (id: string) => {
    setInsulinTypeId(id);
  };

  const handleToggleSide = (side: BodySide) => {
    setBodySide(side);
  };

  const handleSelectSite = (id: number) => {
    setSelectedSiteId(id);
    setCompletionMessage(null);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionDate(event.target.value);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInjectionTime(event.target.value);
  };

  const handleSaveInjection = async () => {
    if (!ageGroup || !selectedSiteId || isSavingInjection || !user) {
      return;
    }
    if (!injectionDate || !injectionTime || !insulinName || insulinDose === '' || !insulinTypeId) {
      setSaveInjectionError('Mohon lengkapi semua data suntikan.');
      return;
    }

    setIsSavingInjection(true);
    setSaveInjectionError(null);
    try {
      await dataService.addInjection(user.id, {
        date: injectionDate,
        time: injectionTime,
        siteId: selectedSiteId,
        insulinTypeId,
        ageGroup,
        insulinBrand: insulinName,
        insulinDose: Number(insulinDose),
        glucoseValue: passedGlucoseValue ?? undefined
      });

      await dataService.updateLastSite(user.id, ageGroup, selectedSiteId);

      const count = getSuggestedSiteCount(ageGroup);
      if (!count) {
        return;
      }
      const currentSelectedId = selectedSiteId;
      const nextId = (currentSelectedId % count) + 1;
      const nextSite = currentSites.find(site => site.id === nextId);
      
      let msg = 'Suntikan berhasil disimpan.';
      if (nextSite) {
        msg = `Suntikan berhasil. Lokasi berikutnya disarankan di ${nextSite.id}: ${nextSite.label}.`;
      }
      setCompletionMessage(msg);
      
      // Delay to let user see completion message before navigating home
      setTimeout(() => navigate('/'), 2500);

    } catch {
      setSaveInjectionError('Terjadi kesalahan saat menyimpan riwayat suntikan.');
    } finally {
      setIsSavingInjection(false);
    }
  };

  const renderLegendModal = () => {
    if (!showLegend || !ageGroup) return null;

    const sortedSites = [...currentSites].sort((a, b) => a.id - b.id);

    return (
      <div className="modal-overlay z-50 fixed inset-0 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowLegend(false)}>
        <div className="modal-content bg-white w-[90%] max-w-[400px] p-5 rounded-[12px] shadow-xl relative" onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="m-0 text-[18px]">Keterangan ({getAgeGroupLabel(ageGroup)})</h3>
            <button
              onClick={() => setShowLegend(false)}
              className="bg-transparent border-none text-[24px] cursor-pointer px-2"
              aria-label="Tutup keterangan"
            >
              &times;
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <ol className="pl-5 m-0 text-left">
              {sortedSites.map((site, index) => (
                <li key={site.id} className="mb-2 text-[14px]">
                  {index + 1}. {site.label}
                </li>
              ))}
            </ol>
            {ageGroup === '2-10' && (
              <p className="mt-4 text-[13px] text-[#666] italic border-t border-[#eee] pt-3 text-left">
                Hindari menyuntikkan insulin di bagian dalam paha, bagian dalam lengan, area yang terlalu dekat dengan pusar, dan pundak
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBodyMap = () => {
    const sites = currentSites
      .filter(site => site.side === bodySide)
      .map(site => ({
        ...site,
        x: bodySide === 'back' ? 100 - site.x : site.x
      }));
    const imageHref =
      bodySide === 'front'
        ? '/assets/images/body-front.png'
        : '/assets/images/body-rear.png';

    const suggestedSite =
      suggestedSiteId !== null
        ? currentSites.find(site => site.id === suggestedSiteId)
        : undefined;

    const selectedSite =
      selectedSiteId !== null
        ? sites.find(site => site.id === selectedSiteId)
        : undefined;

    return (
      <div className="body-card">
        <div className="side-toggle">
          <button
            type="button"
            className={`side-button ${bodySide === 'front' ? 'active' : ''}`}
            onClick={() => handleToggleSide('front')}
          >
            Depan
          </button>
          <button
            type="button"
            className={`side-button ${bodySide === 'back' ? 'active' : ''}`}
            onClick={() => handleToggleSide('back')}
          >
            Belakang
          </button>
        </div>

        <div className="relative w-full max-w-[400px] mx-auto">
          <svg
            className="body-svg block w-full outline outline-1 outline-transparent"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMax meet"
            version="1.1"
          >
            <image href={imageHref} x={26} y={0} height={110} />
            {sites.map(site => (
              <g
                key={site.id}
                className={getSiteClass(site, suggestedSiteId, selectedSiteId)}
                onClick={() => handleSelectSite(site.id)}
              >
                <circle
                  cx={site.x}
                  cy={site.y}
                  r={site.id === suggestedSiteId ? 5 : 4}
                />
                <text
                  x={site.x}
                  y={site.y + 1.2}
                  textAnchor="middle"
                >
                  {site.id}
                </text>
              </g>
            ))}
          </svg>
          {selectedSite ? (
            <div
              className="flying-text -translate-x-1/2 -translate-y-full -mt-[10px]"
              style={{
                left: `${selectedSite.x}%`,
                top: `${selectedSite.y}%`,
                marginLeft: selectedSite.x > 50 ? '-10px' : '10px'
              }}
            >
              {selectedSite.label}
            </div>
          ) : null}
        </div>

        <div className="w-full flex justify-center">
          <button
            type="button"
            className="btn-secondary px-4 py-1.5 text-[13px] rounded-[20px]"
            onClick={() => setShowLegend(true)}
          >
            Keterangan Gambar
          </button>
        </div>

        {suggestedSite ? (
          <div className="suggestion-text">
            <strong>Saran lokasi sekarang: {suggestedSite.id}</strong>
            <span>{suggestedSite.label}</span>
            <div className="text-[13px] italic mt-1 text-[#666]">
              (apabila lokasi suntik sebelumnya sama, maka dapat dipindah)
            </div>
          </div>
        ) : null}

        {renderLegendModal()}
      </div>
    );
  };

  if (ageGroup === null) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan data adik...</div>
      </div>
    );
  }

  const insulinOptions = getInsulinTypeOptions();
  const canSave = selectedSiteId !== null;

  return (
    <div className="screen min-h-[100dvh] h-full flex flex-col">
      <div
        className="screen-card mb-4"
        role="group"
        aria-labelledby="inject-step-title"
      >
        <div id="inject-step-title" className="section-title">
          Suntik insulin
        </div>
        <div className="steps">
          <div className="step-pill active">1. Jenis insulin</div>
          <div className="step-pill">2. Lokasi suntik</div>
          <div className="step-pill">3. Simpan</div>
        </div>

        <div className="field-group">
          <div className="field">
            <label htmlFor="injection-date">Tanggal <span className="text-red-500">*</span></label>
            <input
              id="injection-date"
              type="date"
              value={injectionDate}
              onChange={handleDateChange}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="injection-time">Jam <span className="text-red-500">*</span></label>
            <input
              id="injection-time"
              type="time"
              value={injectionTime}
              onChange={handleTimeChange}
              required
            />
          </div>
        </div>

        <div className="field-group">
          <div className="field">
            <label htmlFor="insulin-name">Nama insulin <span className="text-red-500">*</span></label>
            <input
              id="insulin-name"
              type="text"
              placeholder="Contoh: Levemir"
              value={insulinName}
              onChange={(e) => setInsulinName(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="insulin-dose">Dosis (unit) <span className="text-red-500">*</span></label>
            <input
              id="insulin-dose"
              type="number"
              min={0}
              inputMode="decimal"
              value={insulinDose}
              onChange={(e) => setInsulinDose(e.target.value === '' ? '' : Number(e.target.value))}
              required
            />
          </div>
        </div>

        <div className="subtitle">1. Pilih jenis suntikan <span className="text-red-500">*</span></div>
        <div className="option-grid mb-3">
          {insulinOptions.map(option => (
            <button
              key={option.id}
              type="button"
              className={`chip ${insulinTypeId === option.id ? 'selected' : ''}`}
              onClick={() => handleSelectInsulinType(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="screen-card flex-1 flex flex-col min-h-fit"
        role="group"
        aria-labelledby="inject-location-title"
      >
        <div id="inject-location-title" className="section-title text-[28px] mb-2 font-extrabold pb-1">
          Pilih lokasi suntikan
        </div>
        <div className="subtitle text-[16px]">
          2. Pilih titik di tubuh Adik kemudian simpan.
        </div>

        {renderBodyMap()}

        <div className="next-actions mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            className="btn-secondary w-full md:w-auto"
            onClick={() => navigate('/')}
          >
            Batal
          </button>
          <button
            type="button"
            className="btn-primary w-full md:w-auto flex-1"
            disabled={!canSave || isSavingInjection}
            aria-disabled={!canSave || isSavingInjection}
            aria-label="Simpan lokasi suntikan"
            onClick={handleSaveInjection}
          >
            {isSavingInjection ? 'Menyimpan...' : 'Simpan lokasi suntikan'}
          </button>
        </div>

        {completionMessage ? (
          <div className="completion-message font-medium text-emerald-700 bg-emerald-50 p-3 rounded mt-2 border border-emerald-100">{completionMessage}</div>
        ) : null}
        {saveInjectionError ? (
          <div className="subtitle mt-2 text-red-700 bg-red-50 p-2 rounded">
            {saveInjectionError}
          </div>
        ) : null}
      </div>
    </div>
  );
}
